import { useOnOutsideClick, useEventListener } from '@/hooks'
import {
  DropdownWrapper,
  Input,
  DropdownBox,
  InputAndSelectionContainer,
  AngleDownIcon,
} from './Styles'
import { castArray } from 'lodash'
import { useState, useRef, ReactElement } from 'react'
import ParentDepartmentRow from './ParentDepartmentRow'
import { useQuery } from '@apollo/client'
import { DEPARTMENTS_QUERY } from '@/graphql/queries/Departments'
import SelectedDepartment from './SelectedDepartment'
import { LoadingSpinner } from '@/ui'
import {
  createDepartmentHierarchy,
  GetDepartmentsFromIds,
  filterDepartmentsByName,
  GetAllChildren,
  GetDepartmentFromIndex,
} from './helpers'
import { useForm } from 'react-final-form'
import ErrorLabel from '@/components/Field/ErrorLabel'
import { useTranslation } from 'react-i18next'
import SmallSelection from './SmallSelection'

interface DepartmentsDropdownProps {
  fieldName?: string
  fieldDisabled?: string
  required?: boolean
  disabled?: boolean
  disableToggling?: boolean
  upward?: boolean
  multi?: boolean
  oneline?: boolean
  noMargin?: boolean
  fieldInitialValues?: string[]
  fieldOnChange?: (selectedDepartments: Department[] | Department) => void
  label?: ReactElement
  placeholder?: string
  input?: any
  query?: {
    variables?: any
    filter?: (data: any) => any
  }
  disabledDepartments?: string[] //When a department has been selected, so it can't be selected again, but you dont want to filter it because it ruins the hiearchy
}

export interface Department {
  id: string
  name: string
  path: string
  index: number
  children?: Department[]
  hasChildren?: boolean
  parentDepartment?: {
    id: string
    name: string
    path?: string
  }
}

export default function DepartmentsDropdown({
  multi = true,
  label,
  required,
  disabledDepartments,
  fieldOnChange,
  oneline = false,
  noMargin,
  fieldName = 'departmentIds',
  fieldInitialValues,
  fieldDisabled,
  disableToggling,
  placeholder,
  query,
  upward,
  input,
}: DepartmentsDropdownProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState<string>('')
  const [currentIndex, setCurrentIndex] = useState<number | undefined>(
    undefined,
  )
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState(
    fieldInitialValues ?? [],
  )
  const useLabel = !!label?.props?.label //Label is sometimes set by the parent. If it's not, then it will have a label prop instead
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const selectionBoxRef = useRef<HTMLDivElement>(null)
  const height = selectionBoxRef.current?.clientHeight || 0
  const form = useForm()

  const { data, loading: dataLoading } = useQuery(DEPARTMENTS_QUERY, {
    fetchPolicy: 'no-cache',
    variables: {
      orderBy: 'NAME_ASC',
      ...(query?.variables ?? {}),
    },
  })

  const departmentData = query?.filter
    ? data?.departments?.edges.filter(query.filter)
    : data?.departments?.edges

  const departmentIds: string[] =
    (input ? castArray(input.value) : selectedDepartmentIds) || []

  //Building unfiltered and filtered department hiarchies
  const tree: Department[] = createDepartmentHierarchy(departmentData)
  const filteredDepartments = filterDepartmentsByName(tree, search) || []
  const departmentToUse = search ? filteredDepartments : tree

  const selectedDepartments = GetDepartmentsFromIds(tree, departmentIds)

  const handleOnChange = (newSelectedDepartments: Department[]) => {
    const newDepartmentIds = newSelectedDepartments.map(
      department => department.id,
    )

    if (input) {
      input.onChange(multi ? newDepartmentIds : newDepartmentIds[0])
    } else {
      setSelectedDepartmentIds(newDepartmentIds)
    }

    if (fieldOnChange) {
      fieldOnChange(multi ? newSelectedDepartments : newSelectedDepartments[0])
    }
  }

  useOnOutsideClick(dropdownRef, e => {
    const path = e.composedPath && e.composedPath()
    if (
      (path as Element[]).some(({ className }) =>
        className?.includes?.(' popup '),
      )
    ) {
      return
    }
    setCurrentIndex(undefined)
    setOpen(false)
  })

  function HandleSearch(search: string) {
    setSearch(search)
  }
  function DepartmentsMatchingSearch(departments: Department[]): Department[] {
    let departmentArray: Department[] = []
    for (const department of departments) {
      const matchesSearch = department.name
        .toLowerCase()
        .includes(search.toLowerCase())
      const isSelected = selectedDepartments?.some(
        dep => dep?.id === department.id,
      )
      if (matchesSearch && !isSelected) {
        departmentArray.push(department)
      }
      if (department.children) {
        departmentArray = [
          ...departmentArray,
          ...DepartmentsMatchingSearch(department.children),
        ]
      }
    }
    return departmentArray
  }

  function HandleSelectSingleDepartment(department: Department) {
    const isSelected = selectedDepartments?.some(
      dep => dep?.id === department.id,
    )
    if (isSelected) {
      if (multi) {
        handleOnChange(
          selectedDepartments?.filter(dep => dep?.id !== department.id),
        )
      } else handleOnChange([])
    } else {
      if (multi) {
        handleOnChange([...selectedDepartments, department])
      } else handleOnChange([department])
    }

    if (!multi) {
      setOpen(false)
    }
  }

  //Used to determine if departments without children should be indented to account for other departments with children
  const childrenExists = !!departmentToUse.find(
    department => department.children && department.children.length > 0,
  )

  //It might be necessary to also get submitErrors from form, but at present I cant find any places that allow submission with empty required fields
  const fieldError = form.getFieldState(fieldName)?.error
  const error =
    !open &&
    fieldError &&
    (required || !!fieldError) &&
    !selectedDepartments.length

  useEventListener('keyup', async e => {
    if (document.activeElement !== inputRef.current) {
      return
    }

    const maxLength = !search
      ? data?.departments?.edges?.length || 0
      : GetAllChildren(filteredDepartments).length || 0
    //currentIndex starts off as undefined.
    const currentIndexIsUndefined = typeof currentIndex === 'undefined'

    function ScrollToElement(index: number) {
      const element = document.querySelectorAll(
        `[data-department-row='${index}']`,
      )

      if (element.length > 0) {
        window.requestAnimationFrame(() =>
          element[0].scrollIntoView({
            behavior: 'smooth', // "smooth" can glitch if the user manually scrolls. switch to "instant" or "auto" if it becomes a problem.
            block: 'center',
          }),
        )
      }
    }

    if (e.key === 'ArrowDown') {
      if (!currentIndexIsUndefined && currentIndex >= maxLength - 1) {
        return
      }
      const index = currentIndexIsUndefined ? 0 : currentIndex + 1
      ScrollToElement(index)
      setCurrentIndex(index)
    }

    if (e.key === 'ArrowUp') {
      if (!currentIndexIsUndefined && currentIndex <= 0) {
        return
      }
      const index = currentIndexIsUndefined ? 0 : currentIndex - 1
      ScrollToElement(index)
      setCurrentIndex(index)
    }

    if (e.key === 'Enter') {
      const chosenDepartment =
        !currentIndexIsUndefined &&
        GetDepartmentFromIndex(departmentToUse, currentIndex)
      if (chosenDepartment) {
        HandleSelectSingleDepartment(chosenDepartment)
      }
    }
  })

  return (
    <DropdownWrapper
      ref={dropdownRef}
      disabled={!!fieldDisabled}
      noMargin={noMargin}>
      {useLabel && <>{label}</>}
      <div ref={selectionBoxRef} style={{ position: 'relative' }}>
        <InputAndSelectionContainer
          error={error}
          open={open}
          oneline={oneline}
          onClick={() => inputRef.current?.focus()}>
          {selectedDepartments.length > 0 &&
            !oneline &&
            selectedDepartments.map(department => (
              <SelectedDepartment
                canRemove={!disableToggling}
                deselect={() => {
                  handleOnChange(
                    selectedDepartments.filter(
                      selectedDepartment =>
                        selectedDepartment.id !== department.id,
                    ),
                  )
                }}
                department={department}
                key={department.id}></SelectedDepartment>
            ))}
          {selectedDepartments.length > 0 && oneline && (
            <SmallSelection departments={selectedDepartments} />
          )}
          <Input
            oneline={oneline}
            placeholder={selectedDepartments.length === 0 ? placeholder : ''}
            ref={inputRef}
            className="test"
            onFocus={e => {
              if (disableToggling) {
                // if we set disabled on the input itself, it will not be clickable, which means future data popup can't be toggled.
                // so we just have to blur it immediately.
                e.currentTarget.blur()
              } else {
                setOpen(true)
              }
            }}
            onKeyDown={e => {
              if (e.key.slice(0, 5) !== 'Arrow' && e.key !== 'Enter') {
                setCurrentIndex(undefined)
              } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault()
              }
            }}
            onKeyUp={e => {
              if (
                e.key === 'Enter' &&
                search &&
                filteredDepartments.length > 0 &&
                currentIndex === undefined
              ) {
                //Select the first child matching search
                const possibleDepartments =
                  DepartmentsMatchingSearch(filteredDepartments)
                if (possibleDepartments.length === 0) return
                HandleSelectSingleDepartment(possibleDepartments[0])
              }
              if (e.key === 'Backspace' && search === '') {
                handleOnChange(
                  selectedDepartments.slice(0, selectedDepartments.length - 1),
                )
              }
              HandleSearch((e.target as HTMLInputElement).value)
            }}></Input>
          <AngleDownIcon
            onClick={e => {
              e.stopPropagation()
              setOpen(!open)
            }}
          />
        </InputAndSelectionContainer>
        {error && <ErrorLabel error={t(fieldError)} />}

        {open && (
          <DropdownBox upward={upward} oneline={oneline} height={height + 1}>
            {dataLoading && <LoadingSpinner />}
            {(search ? filteredDepartments || [] : tree).map(department => (
              <ParentDepartmentRow
                department={department}
                searchTerm={search.toLowerCase()} //If the user has searched, expand relevant departments
                currentIndex={currentIndex}
                allowArrows={childrenExists}
                key={department.id}
                selectedDepartments={selectedDepartments}
                onChange={handleOnChange}
                handleSelectSingleDepartment={HandleSelectSingleDepartment}
                multiple={multi}
                disabledDepartments={disabledDepartments}
              />
            ))}
          </DropdownBox>
        )}
      </div>
    </DropdownWrapper>
  )
}
